import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import AnimatedSection from '../../components/common/AnimatedSection';

// 样式组件
const DetailContainer = styled('div', {
  padding: '0 20px',
});

const NewsArticle = styled('article', {
  background: '$bgSecondary',
  borderRadius: '15px',
  padding: '40px',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
  maxWidth: '900px',
  margin: '0 auto',
  
  '@media (max-width: 768px)': {
    padding: '30px 20px',
  }
});

const ArticleHeader = styled('div', {
  marginBottom: '40px',
  
  '& .article-category': {
    display: 'inline-block',
    padding: '5px 12px',
    borderRadius: '20px',
    background: '#F0F4FF',
    color: '#3563E9',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '15px',
  },
  
  '& .article-title': {
    fontSize: '32px',
    fontWeight: '700',
    color: '$headingColor',
    marginBottom: '20px',
    lineHeight: '1.3',
    
    '@media (max-width: 768px)': {
      fontSize: '26px',
    }
  },
  
  '& .article-meta': {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    flexWrap: 'wrap',
    fontSize: '14px',
    color: '$textSecondary',
    
    '& .separator': {
      width: '4px',
      height: '4px',
      borderRadius: '50%',
      background: '$textSecondary',
      display: 'inline-block',
    }
  }
});

const ArticleImage = styled('div', {
  borderRadius: '10px',
  overflow: 'hidden',
  marginBottom: '40px',
  background: '#F0F4FF',
  height: '400px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#3563E9',
  fontSize: '24px',
  
  '@media (max-width: 768px)': {
    height: '250px',
  }
});

const ArticleContent = styled('div', {
  fontSize: '17px',
  lineHeight: '1.8',
  color: '$textPrimary',
  
  '& p': {
    marginBottom: '25px',
  },
  
  '& h2': {
    fontSize: '24px',
    fontWeight: '600',
    color: '$headingColor',
    marginTop: '40px',
    marginBottom: '20px',
  },
  
  '& h3': {
    fontSize: '20px',
    fontWeight: '600',
    color: '$headingColor',
    marginTop: '30px',
    marginBottom: '15px',
  },
  
  '& ul, & ol': {
    paddingLeft: '25px',
    marginBottom: '25px',
    
    '& li': {
      marginBottom: '10px',
    }
  },
  
  '& blockquote': {
    borderLeft: '4px solid #3563E9',
    paddingLeft: '20px',
    fontStyle: 'italic',
    margin: '30px 0',
    color: '$textSecondary',
  }
});

const ArticleTags = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  marginTop: '40px',
  paddingTop: '30px',
  borderTop: '1px solid #eaeaea',
});

const ArticleTag = styled('span', {
  padding: '5px 12px',
  borderRadius: '20px',
  background: '#F0F4FF',
  color: '#3563E9',
  fontSize: '14px',
  fontWeight: '500',
});

const RelatedArticles = styled('div', {
  marginTop: '60px',
});

const RelatedArticlesList = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
  gap: '30px',
  marginTop: '30px',
});

const RelatedArticleCard = styled(Link, {
  display: 'flex',
  flexDirection: 'column',
  textDecoration: 'none',
  background: '$bgSecondary',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  },
  
  '& .card-image': {
    height: '150px',
    background: '#F0F4FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#3563E9',
  },
  
  '& .card-content': {
    padding: '20px',
  },
  
  '& .card-title': {
    fontSize: '17px',
    fontWeight: '600',
    color: '$headingColor',
    marginBottom: '10px',
  },
  
  '& .card-date': {
    fontSize: '14px',
    color: '#3563E9',
  }
});

const BackToNews = styled(Link, {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  margin: '40px 0',
  color: '#3563E9',
  fontWeight: '500',
  textDecoration: 'none',
  
  '&:hover': {
    textDecoration: 'underline',
  }
});

// 新闻详情页面组件
const NewsDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  
  // 这里可以通过id从API获取新闻详情
  // 示例数据
  const article = {
    id,
    title: t('news.detail.title'),
    category: t('news.detail.category'),
    date: t('news.detail.date'),
    author: t('news.detail.author'),
    readTime: t('news.detail.readTime'),
    tags: [
      t('news.tags.bitcoin'),
      t('news.tags.mining'),
      t('news.tags.technology')
    ],
    content: [
      { type: 'paragraph', content: t('news.detail.paragraph1') },
      { type: 'paragraph', content: t('news.detail.paragraph2') },
      { type: 'heading', content: t('news.detail.heading1') },
      { type: 'paragraph', content: t('news.detail.paragraph3') },
      { type: 'paragraph', content: t('news.detail.paragraph4') },
      { type: 'heading', content: t('news.detail.heading2') },
      { type: 'paragraph', content: t('news.detail.paragraph5') },
      { type: 'blockquote', content: t('news.detail.blockquote') },
      { type: 'paragraph', content: t('news.detail.paragraph6') }
    ]
  };
  
  // 示例相关文章
  const relatedArticles = [
    {
      id: '2',
      title: t('news.relatedArticles.article1.title'),
      date: t('news.relatedArticles.article1.date')
    },
    {
      id: '3',
      title: t('news.relatedArticles.article2.title'),
      date: t('news.relatedArticles.article2.date')
    },
    {
      id: '4',
      title: t('news.relatedArticles.article3.title'),
      date: t('news.relatedArticles.article3.date')
    }
  ];
  
  return (
    <DetailContainer>
      <AnimatedSection direction="up" delay={0.2}>
        <BackToNews to="/news">
          ← {t('news.backToNews')}
        </BackToNews>
        
        <NewsArticle>
          <ArticleHeader>
            <div className="article-category">{article.category}</div>
            <h1 className="article-title">{article.title}</h1>
            <div className="article-meta">
              <span>{article.date}</span>
              <span className="separator"></span>
              <span>{article.author}</span>
              <span className="separator"></span>
              <span>{article.readTime}</span>
            </div>
          </ArticleHeader>
          
          <ArticleImage>
            📰 {t('news.imageDetailPlaceholder')}
          </ArticleImage>
          
          <ArticleContent>
            {article.content.map((block, index) => {
              switch (block.type) {
                case 'heading':
                  return <h2 key={index}>{block.content}</h2>;
                case 'paragraph':
                  return <p key={index}>{block.content}</p>;
                case 'blockquote':
                  return <blockquote key={index}>{block.content}</blockquote>;
                default:
                  return null;
              }
            })}
            
            <ArticleTags>
              {article.tags.map((tag, index) => (
                <ArticleTag key={index}>{tag}</ArticleTag>
              ))}
            </ArticleTags>
          </ArticleContent>
        </NewsArticle>
        
        <RelatedArticles>
          <h3 className="section-subtitle">{t('news.relatedArticlesTitle')}</h3>
          <RelatedArticlesList>
            {relatedArticles.map(article => (
              <RelatedArticleCard key={article.id} to={`/news/${article.id}`}>
                <div className="card-image">📰</div>
                <div className="card-content">
                  <div className="card-title">{article.title}</div>
                  <div className="card-date">{article.date}</div>
                </div>
              </RelatedArticleCard>
            ))}
          </RelatedArticlesList>
        </RelatedArticles>
      </AnimatedSection>
    </DetailContainer>
  );
};

export default NewsDetail; 